import * as Sentry from "@sentry/react";

/**
 * Fallback DSN. Mantiene paridad con el `Sentry.init` previo en
 * `src/router/router.jsx` para no romper la captura existente cuando
 * el env var `REACT_APP_SENTRY_DSN` no está seteado todavía.
 */
const DEFAULT_DSN =
  "https://45c7136071640000b70caa54657e6007@o4506593438924800.ingest.sentry.io/4506713512869888";

let initialized = false;

/**
 * Inicializa Sentry React con configuración endurecida. Idempotente.
 *
 * Diseño conservador:
 *  - DSN desde `options.dsn` o `REACT_APP_SENTRY_DSN`, con fallback al hardcoded
 *    actual (sin breaking change). Setear el env var a `""` (string vacío) o
 *    pasar `options.dsn = ""` deshabilita Sentry sin tocar este archivo.
 *  - environment desde REACT_APP_SENTRY_ENVIRONMENT (cada .env.* define el suyo)
 *  - release desde REACT_APP_SENTRY_RELEASE (idealmente commit SHA en CI)
 *  - tracesSampler dinámico: respeta `parentSampled` (traces distribuidos),
 *    20% en hot paths (sendMessage/broadcast/conversation), 0 en healthchecks,
 *    5% baseline, 0 en `development`
 *  - replayIntegration con `replaysOnErrorSampleRate: 1.0`, `replaysSessionSampleRate: 0.05`,
 *    `maskAllText: true` y `blockAllMedia: true` para no exponer PII (teléfonos,
 *    nombres, mensajes) que el dashboard renderiza
 *  - tracePropagationTargets: backend + omnichannel
 *  - beforeSend con PII scrubbing (tokens en query string, Authorization, phone)
 *  - maxBreadcrumbs: 50
 *
 * Llamar desde `src/index.js` ANTES de `ReactDOM.render`.
 *
 * Idempotencia: el flag interno solo se setea tras un `Sentry.init` exitoso.
 * Si `Sentry.init` lanza, el flag queda en `false` y un siguiente llamado
 * puede reintentar.
 *
 * @param {Object} [options]
 * @param {import('history').History} [options.history] History de react-router para
 *   habilitar `reactRouterV5BrowserTracingIntegration`. Si no se provee, el tracing
 *   por navegación queda deshabilitado (los spans manuales y la captura siguen activos).
 * @param {string} [options.dsn] Override explícito del DSN. Si se pasa `""` o `null`,
 *   Sentry queda deshabilitado.
 * @returns {void}
 */
export function initSentry(options = {}) {
  if (initialized) return;

  // Resolución del DSN:
  //   - `options.dsn` toma precedencia (incluso si es `""`/`null` → disabled explícito)
  //   - luego `REACT_APP_SENTRY_DSN` (igualmente: `""` → disabled explícito)
  //   - finalmente fallback al hardcoded para no romper captura existente
  let dsn;
  if (Object.prototype.hasOwnProperty.call(options, "dsn")) {
    dsn = options.dsn;
  } else if (process.env.REACT_APP_SENTRY_DSN !== undefined) {
    dsn = process.env.REACT_APP_SENTRY_DSN;
  } else {
    dsn = DEFAULT_DSN;
  }
  if (!dsn) {
    // eslint-disable-next-line no-console
    console.warn("[sentry] DSN empty — Sentry disabled");
    return;
  }

  const environment =
    process.env.REACT_APP_SENTRY_ENVIRONMENT ||
    process.env.REACT_APP_ENV ||
    process.env.NODE_ENV ||
    "development";

  const release = process.env.REACT_APP_SENTRY_RELEASE;

  const integrations = [];
  if (options.history && typeof Sentry.reactRouterV5BrowserTracingIntegration === "function") {
    integrations.push(
      Sentry.reactRouterV5BrowserTracingIntegration({ history: options.history })
    );
  }
  // Replay: 100% en error, 5% sesiones random. Importante para investigar
  // sesiones de agentes que reporten bugs sin necesidad de pedirles repro.
  if (typeof Sentry.replayIntegration === "function") {
    integrations.push(
      Sentry.replayIntegration({
        // Mask por defecto: el dashboard renderiza PII (teléfonos, nombres,
        // mensajes). El scrubbing de `beforeSend` NO aplica al payload de Replay.
        // Si se necesita unmasking selectivo en vistas seguras, marcar nodos con
        // la clase `sentry-unmask` (ver docs de Replay).
        maskAllText: true,
        blockAllMedia: true,
      })
    );
  }

  try {
    Sentry.init({
      dsn,
      environment,
      release,
      integrations,
      tracesSampler: makeSampler(environment),
      replaysSessionSampleRate: 0.05,
      replaysOnErrorSampleRate: 1.0,
      // Patrones que reciben los headers `sentry-trace` y `baggage` para
      // continuación de traza distribuida. Cubrir TODOS los hosts que el
      // dashboard golpea para que cualquier llamada quede enlazada al
      // backend en Sentry:
      //   - graph[*].iventas        → GraphQL (graph.iventas.com,
      //                                gabograph.iventas.mx, arturograph,
      //                                danograph.iventas.mx)
      //   - restservices.iventas    → REST principal (src/axios.js)
      //   - <user>restservices.iventas.mx → REST por dev (arturo/gabo/dano)
      //   - payments[*].iventas     → REST payments (src/axiosPayments.js)
      //                                (payments.iventas.mx,
      //                                payments-development.iventas.mx,
      //                                payments-dano.iventas.mx)
      //   - boards[*].iventas       → boards API + Pusher auth endpoint
      //   - iv-analytics-service*.herokuapp.com → microservicio de analytics
      //                                (REACT_APP_ANALYTICS_API_URL,
      //                                src/services/analyticsDashboard.service.js).
      //                                Ahora que ese servicio valida el JWT y
      //                                hace tenant-scoping (ver PR de backend
      //                                iVentas-Plus/iventas-analytics#1), se
      //                                propaga sentry-trace/baggage para poder
      //                                correlacionar la request del dashboard
      //                                con los spans del backend de analytics.
      //   - localhost:3002/3006     → dev local del backend
      //   - api.iventas / omnichannel → reservados para futuros endpoints
      // Si se agrega un endpoint nuevo (p.ej. analytics.iventas), añadirlo
      // aquí para no perder propagación.
      tracePropagationTargets: [
        /graph.*\.iventas/i,
        /restservices\.iventas/i,
        /payments.*\.iventas/i,
        /boards.*\.iventas/i,
        /iv-analytics-service.*\.herokuapp\.com/i,
        /localhost:300[26]/i,
        /api\.iventas/i,
        /omnichannel/i,
      ],
      normalizeDepth: 5,
      maxBreadcrumbs: 50,
      sendDefaultPii: false,
      beforeSend: scrubPii,
      beforeSendTransaction: scrubPii,
      ignoreErrors: [
        // Ruido conocido de browsers/extensions/redes
        /ResizeObserver loop limit/,
        /ResizeObserver loop completed with undelivered notifications/,
        /Non-Error promise rejection captured/,
        /Network request failed/,
        /Failed to fetch/,
        /Load failed/,
      ],
    });
  } catch (e) {
    // No bloquees retries futuros si init falló. No relanzar — Sentry roto
    // no debe tirar la app.
    // eslint-disable-next-line no-console
    console.warn("[sentry] init failed:", e && e.message);
    return;
  }

  initialized = true;
  // Tag global para queries cross-proyecto en Sentry (backend, mobile, dashboard).
  Sentry.setTag("service", "dashboard");
}

/**
 * Sampler dinámico. Política real implementada (mantener docstring sincronizado
 * con el cuerpo):
 *  - `environment === 'development'` → 0 (no trazamos en dev local).
 *  - `parentSampled` definido → se respeta (1 si parent sampled, 0 si no);
 *    mantiene traces distribuidos consistentes con el backend.
 *  - `name` matches `/health|ping|metrics/i` → 0 (sin valor diagnóstico).
 *  - `name` matches `/sendMessage|broadcast|conversation/i` → 0.2 (hot paths).
 *  - default → 0.05 (baseline para prod/staging).
 *
 * Nota: este sampler es para `transactions/traces`. La captura de errores
 * (`captureException`/`captureMessage`) NO depende de él — los errores siempre
 * se envían (sujetos a `ignoreErrors` y `beforeSend`).
 *
 * @param {string} environment
 * @returns {(ctx: import('@sentry/react').SamplingContext) => number}
 */
function makeSampler(environment) {
  return (ctx) => {
    if (environment === "development") return 0;

    // Si el cliente continúa una traza distribuida, respetar parentSampled
    if (ctx && ctx.parentSampled !== undefined && ctx.parentSampled !== null) {
      return ctx.parentSampled ? 1 : 0;
    }

    const name = (ctx && ctx.name) || "";

    // Healthchecks o navegaciones a /health → 0
    if (/health|ping|metrics/i.test(name)) return 0;

    // Hot paths críticos
    if (/sendMessage|broadcast|conversation/i.test(name)) return 0.2;

    return 0.05;
  };
}

/**
 * Limpia campos PII de eventos antes de enviar a Sentry. Defensivo: si
 * la forma del evento no coincide con lo esperado, retorna sin cambios.
 *
 * Importante: el dashboard puede recibir tokens en query strings (magic links,
 * callbacks OAuth, parámetros con `token=`/`api_key=`/`password=`/etc).
 * `scrubPii` cubre `request.query_string` independientemente de qué
 * integraciones default de `@sentry/browser` estén activas — no asumimos
 * `requestDataIntegration` con `query_string: false`.
 *
 * @param {import('@sentry/react').Event | null | undefined} event
 * @returns {import('@sentry/react').Event | null}
 */
function scrubPii(event) {
  if (!event) return null;
  try {
    // request.data: phone, token, body, password
    if (event.request && event.request.data && typeof event.request.data === "object") {
      const data = event.request.data;
      [
        "phone",
        "to",
        "from",
        "messageBody",
        "body",
        "text",
        "token",
        "password",
        "apiKey",
        "api_key",
      ].forEach((k) => {
        if (k in data) delete data[k];
      });
    }
    // request.headers (case-insensitive)
    if (event.request && event.request.headers && typeof event.request.headers === "object") {
      const PII = new Set(["authorization", "cookie", "x-api-key", "x-auth-token"]);
      Object.keys(event.request.headers).forEach((k) => {
        if (PII.has(k.toLowerCase())) delete event.request.headers[k];
      });
    }
    // request.query_string: tokens pueden colarse en magic links / OAuth callbacks.
    // El boundary `(^|[?&])` cubre el primer parámetro cuando el string viene
    // sin `?` inicial (p.ej. `token=xxx&x=y`). Mantenemos el separador en $1
    // para que el query string siga siendo válido tras el scrub.
    if (event.request && typeof event.request.query_string === "string") {
      event.request.query_string = event.request.query_string.replace(
        /(^|[?&])((?:token|access_token|api_key|apiKey|password)=)[^&]*/gi,
        "$1$2[Filtered]"
      );
    }
    // extra: por si algún captureException acompañó context con tokens
    if (event.extra && typeof event.extra === "object") {
      ["token", "password", "apiKey", "api_key", "authorization"].forEach((k) => {
        if (k in event.extra) delete event.extra[k];
      });
    }
  } catch (e) {
    // Nunca dejes que scrubbing tire el evento entero — preferimos enviar el
    // evento posiblemente con PII parcial a perderlo. El catch sale al return
    // de abajo manteniendo el `event` (posiblemente parcialmente mutado).
    // eslint-disable-next-line no-console
    console.warn(
      "[sentry] scrubPii threw, returning event without further scrubbing:",
      e && e.message
    );
  }
  return event;
}

export { scrubPii };
